import React, { ReactElement } from 'react'
import { Helmet } from 'react-helmet-async'
import { useStaticQuery, graphql } from 'gatsby'
import { WithContext, Thing } from 'schema-dts'

export interface SEOProps {
  /**
   * Meta description for the page
   * @default `siteMetadata.description`
   */
  description?: string
  /**
   * Language of page content, <html lang={lang}>
   * @default en
   */
  lang?: string
  /** Additional meta tags to insert into <head>.
   * @example [{ name: 'some:meta', content: 'cool content' }]
   */
  meta?: {
    name?: string
    property?: string
    content: string
  }[]
  /**
   * Page title, shown in tab/window
   * @default `siteMetadata.title`
   */
  title: string
  /**
   * Creator of the page
   * @default `siteMetadata.author`
   */
  author?: string
  /**
   * JSON-LD object defining data on the page - Use schema-dts types to ensure validity
   */
  jsonLD?: WithContext<Thing>
  /**
   * Whether to add robots - noindex, nofollow tag
   * @default false
   */
  noindex?: boolean
}

interface SiteQuery {
  site: {
    siteMetadata: {
      title: string
      description: string
      author: string
    }
  }
}
const siteQuery = graphql`
  query {
    site {
      siteMetadata {
        title
        description
        author
      }
    }
  }
`

/**
 * Renders site/page metadata to the page head.
 */
export function SEO({
  author,
  description,
  lang = 'en',
  meta = [],
  title,
  noindex = false,
  jsonLD,
}: SEOProps): ReactElement {
  const { site } = useStaticQuery<SiteQuery>(siteQuery)

  const data = {
    author: author || site.siteMetadata.author,
    description: description || site.siteMetadata.description,
    lang,
    meta,
    pageTitle: title,
    siteTitle: site.siteMetadata.title,
    noindex,
    jsonLD,
  }

  return <PureSEO {...data} />
}

type PureSEOProps = Omit<SEOProps, 'title'> & {
  pageTitle?: string
  siteTitle: string
  jsonLD: unknown
}
export function PureSEO(props: PureSEOProps): ReactElement {
  const {
    lang = 'en',
    siteTitle,
    pageTitle,
    description,
    author,
    noindex,
    meta = [],
    jsonLD,
  } = props
  if (noindex) meta.push({ name: 'robots', content: 'noindex, nofollow' })
  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={pageTitle || siteTitle}
      meta={[
        {
          name: `description`,
          content: description,
        },
        {
          property: `og:title`,
          content: pageTitle,
        },
        {
          property: `og:description`,
          content: description,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: author,
        },
        {
          name: `twitter:title`,
          content: pageTitle,
        },
        {
          name: `twitter:description`,
          content: description,
        },
        ...meta,
      ]}
    >
      {jsonLD ? (
        <script type="application/ld+json">{JSON.stringify(jsonLD)}</script>
      ) : null}
    </Helmet>
  )
}
